import React, { useContext } from 'react';
import { AppContext } from '../../../../common';
import { EmployeeNodeModel } from '../../../../models';
import { DayModel, OvertimeWorkedAndScheduledPerPeriodModel } from '../../models/OvertimeSchedule';
import { PeriodDate } from '../../utils/period.utils';
import { normalizeDateString } from '../../utils/date.utils';
import { DayOvertimeValueCell } from './cells/DayOvertimeValueCell';
import { EmployeeInfoCell } from './cells/EmployeeInfoCell';
import { ResumenMetricCell } from './cells/ResumenMetricCell';
import { RESUMEN_COLUMNS } from './resumenColumns.config';

const ROW_CELL_STYLE = {
  padding: '0.3rem',
  height: '3.5rem',
};

interface BodyProps {
  data: OvertimeWorkedAndScheduledPerPeriodModel[];
  periodDates: PeriodDate[];
  selectedPeriodIndex: number;
  disabled?: boolean;
}

const buildDayLookup = (days: DayModel[] = []) => {
  const map = new Map<string, DayModel>();
  days.forEach((day) => map.set(normalizeDateString(day.date), day));
  return map;
};

export const EmployeeTableBody = ({ data }: Pick<BodyProps, 'data'>) => (
  <tbody>
    {data.map((employee) => (
      <tr key={employee.employeeId}>
        <td className="align-middle " style={{ ...ROW_CELL_STYLE, width: '170px' }}>
          <EmployeeInfoCell employee={employee} />
        </td>
      </tr>
    ))}
  </tbody>
);

export const DaysTableBody = ({ data, periodDates, selectedPeriodIndex, disabled = false }: BodyProps) => {
  const { handlerOpenWorkshiftModal, handlerOpenDeleteWorkshiftModal } = useContext(AppContext);

  const iconStyle = {
    bottom: 1,
    fontSize: '0.65rem',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1,
    pointerEvents: disabled ? ('none' as const) : ('auto' as const),
  };

  const handleOpenWorkshift = (date: string, employee: OvertimeWorkedAndScheduledPerPeriodModel) => {
    if (disabled) return;
    const employeeNode: EmployeeNodeModel = {
      id: employee.employeeId,
      parent: 0,
      text: employee.fullName,
      documentNumber: employee.documentNumber,
      cardNumber: employee.cardNumber,
    };
    handlerOpenWorkshiftModal(date, date, [employeeNode], false);
  };

  const handleDeleteOpenWorkshift = (
    date: string,
    employee: OvertimeWorkedAndScheduledPerPeriodModel,
    workshiftId: number,
  ) => {
    if (disabled) return;
    const employeeNode: EmployeeNodeModel = {
      id: employee.employeeId,
      parent: 0,
      text: employee.fullName,
      documentNumber: employee.documentNumber,
      cardNumber: employee.cardNumber,
    };
    handlerOpenDeleteWorkshiftModal(date, date, [employeeNode], [workshiftId]);
  };

  return (
    <tbody>
      {data.map((employee) => {
        const period = employee.periods?.[selectedPeriodIndex];
        const dayLookup = buildDayLookup(period?.days ?? []);

        return (
          <tr key={employee.employeeId}>
            {periodDates.map((periodDate) => {
              const day = dayLookup.get(periodDate.dateKey);

              return (
                <td
                  key={periodDate.dateKey}
                  colSpan={2}
                  className="position-relative align-top overtime-schedule-day-col"
                  style={ROW_CELL_STYLE}
                  title={periodDate.label}
                >
                  <i
                    id="openWorkshiftModal"
                    className={`mdi mdi-calendar-plus ${day?.workshiftId ? 'text-success' : 'text-muted'} position-absolute`}
                    title={`${day?.workshiftId ? 'Cambiar turno: ' + day.workshiftAlias : 'Asignar Turno'}`}
                    style={{
                      ...iconStyle,
                      right: day?.workshiftId ? 32 : 18,
                    }}
                    onClick={() => handleOpenWorkshift(periodDate.dateKey, employee)}
                  />

                  {day?.workshiftId && (
                    <i
                      id="deleteWorkshiftModal"
                      className="mdi mdi-calendar-remove text-danger position-absolute"
                      title="Eliminar turno"
                      style={{
                        ...iconStyle,
                        right: 18,
                      }}
                      onClick={() =>
                        handleDeleteOpenWorkshift(periodDate.dateKey, employee, day.workshiftSchedulingId!)
                      }
                    />
                  )}

                  <DayOvertimeValueCell day={day} compact />
                </td>
              );
            })}
          </tr>
        );
      })}
    </tbody>
  );
};

export const ResumenTableBody = ({ data, selectedPeriodIndex }: Pick<BodyProps, 'data' | 'selectedPeriodIndex'>) => (
  <tbody>
    {data.map((employee) => {
      const period = employee.periods?.[selectedPeriodIndex];

      return (
        <tr key={employee.employeeId}>
          {RESUMEN_COLUMNS.map((column) => (
            <td
              key={column.key}
              className="bg-light position-relative align-top overtime-schedule-day-col"
              style={ROW_CELL_STYLE}
              title={column.title}
            >
              <ResumenMetricCell
                value={period ? column.getValue(period) : null}
                validation={period ? column.getValidation(period) : 'default'}
              />
            </td>
          ))}
        </tr>
      );
    })}
  </tbody>
);
